/* =============================================================================
File: EasyStumpOut_HomePage_v01.tsx
Description: ReactSetv22026BSSC homepage for EasyStumpOut.
Version: v01
Date: 2026-04-14
============================================================================= */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  Button,
  Carousel,
  Col,
  Container,
  Form,
  Row,
} from "react-bootstrap";

import { Link, useLocation } from "react-router-dom";


import UI_ToolTipOnHover from "../../00UI-Utilities/toolTipOnHover";
import EasyStumpOut_Header from "./EasyStumpOut_Header_v01_active";
import EasyStumpOut_Footer from "./EasyStumpOut_Footer_v01_active";

import { type ILocationConfig } from "../App_CONFIG_v01a";

//CSS CUSTOM STYLES!
import { InlinePageStyles } from "../App_STYLES_v01a";



type locationConfigProps = {
  locationConfig: ILocationConfig;
};


type HeroSlide = {
  title: string;
  description: string;
  imageUrl: string;
  primaryCtaText: string;
  primaryCtaHref: string;
  secondaryCtaText: string;
  secondaryCtaHref: string;
};

type HeroCarouselData = {
  brandKicker: string;
  slides: HeroSlide[];
};

type FaqItem = {
  id: string;
  question: string;
  answer: React.ReactNode;
  defaultOpen?: boolean;
};

type QuoteFormValues = {
  name: string;
  phone: string;
  email: string;
  city: string;
  count: string;
  diameter: string;
  access: string;
  message: string;
  smsMarketingConsent: boolean;
  smsNonMarketingConsent: boolean;
};

type WirePayload = Record<string, string>;

function toWirePayloadStrings(payload: Record<string, unknown>): WirePayload {
  const out: WirePayload = {};
  for (const [k, v] of Object.entries(payload)) {
    if (v === null || v === undefined) out[k] = "";
    else if (typeof v === "string") out[k] = v;
    else if (typeof v === "number") out[k] = Number.isFinite(v) ? String(v) : "";
    else if (typeof v === "boolean") out[k] = v ? "true" : "false";
    else if (v && typeof v === "object") {
      try {
        out[k] = JSON.stringify(v);
      } catch {
        out[k] = "";
      }
    } else out[k] = "";
  }
  return out;
}

async function postLead(
  apiUrl: string,
  payload: WirePayload
): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
  const res = await fetch(apiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    mode: "cors",
  });

  let resJson: unknown = null;
  try {
    const txt = await res.text();
    resJson = txt ? JSON.parse(txt) : null;
  } catch {
    resJson = null;
  }

  const asObj = (x: unknown): Record<string, unknown> | null =>
    x && typeof x === "object" && !Array.isArray(x)
      ? (x as Record<string, unknown>)
      : null;

  if (!res.ok) {
    const o = asObj(resJson);
    const msg =
      (o?.error as string) ||
      (o?.message as string) ||
      `Request failed: ${res.status}`;
    return { ok: false, status: res.status, message: msg };
  }

  const o = asObj(resJson);
  const id = (o?.id as string) || (o?.requestId as string) || undefined;
  return { ok: true, status: res.status, id };
}

function useInjectHeadMetadata(locationConfig: ILocationConfig) {
  useEffect(() => {
    document.title = locationConfig.metaTitle;

    const upsertMeta = (attrs: Record<string, string>) => {
      const selector = Object.entries(attrs)
        .filter(([k]) => k === "name" || k === "property")
        .map(([k, v]) => `${k}="${v}"`)
        .join("");

      let el = document.head.querySelector<HTMLMetaElement>(`meta[${selector}]`);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
      }
      return el;
    };

    upsertMeta({ name: "viewport", content: "width=device-width, initial-scale=1" });
    upsertMeta({
      name: "description",
      content: locationConfig.metaDescription,
    });
    upsertMeta({ name: "robots", content: "index,follow" });

    upsertMeta({
      property: "og:title",
      content: locationConfig.ogTitle,
    });
    upsertMeta({
      property: "og:description",
      content: locationConfig.ogDescription,
    });
    upsertMeta({ property: "og:type", content: "website" });

    const schemaId = "ldjson-local-business";
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();

    const ld = document.createElement("script");
    ld.id = schemaId;
    ld.type = "application/ld+json";
    ld.text = JSON.stringify(
      {
        "@context": "https://schema.org",
        "@type": locationConfig.schemaType,
        name: locationConfig.businessName,
        description: locationConfig.schemaDescription,
        areaServed: locationConfig.areaServed,
        telephone: locationConfig.phoneHref.replace("tel:", ""),
        url: locationConfig.websiteUrl,
        priceRange: locationConfig.priceRange,
        image: locationConfig.image,
        address: {
          "@type": "PostalAddress",
          streetAddress: locationConfig.address.streetAddress,
          addressLocality: locationConfig.address.addressLocality,
          addressRegion: locationConfig.address.addressRegion,
          postalCode: locationConfig.address.postalCode,
          addressCountry: locationConfig.address.addressCountry,
        },
      },
      null,
      2
    );
    document.head.appendChild(ld);

    return () => {
      const cleanup = document.getElementById(schemaId);
      if (cleanup) cleanup.remove();
    };
  }, [locationConfig]);
}

function HeroCarousel({ data }: { data: HeroCarouselData }) {
  return (
    <header className="hero-carousel">
      <Carousel fade interval={6500} controls indicators>
        {data.slides.map((s, idx) => (
          <Carousel.Item key={`${idx}-${s.title}`}>
            <img className="hero-img" src={s.imageUrl} alt={s.title} />
            <div className="overlay-scrim" aria-hidden="true" />
            <div className="hero-content">
              <Container>
                <div className="hero-kicker">{data.brandKicker}</div>
                <h1>{s.title}</h1>
                <p>{s.description}</p>
                <div className="d-flex flex-wrap gap-2">
                  <a className="btn btn-primary btn-lg" href={s.primaryCtaHref}>
                    {s.primaryCtaText}
                  </a>
                  <a className="btn btn-outline-light btn-lg" href={s.secondaryCtaHref}>
                    {s.secondaryCtaText}
                  </a>
                </div>
                <div className="trust-badges">
                  <span>
                    <span className="dot" />
                    Licensed & insured
                  </span>
                  <span>
                    <span className="dot" />
                    Free quotes
                  </span>
                  <span>
                    <span className="dot" />
                    Clean finish
                  </span>
                </div>
              </Container>
            </div>
          </Carousel.Item>
        ))}
      </Carousel>
    </header>
  );
}





function ServicesSection({ phoneNumber }: { phoneNumber: string }) {
  return (
    <section className="py-5" id="services">
      <Container>
        <Row className="align-items-end g-4">
          <Col lg={7}>
            <h2 className="section-title display-6 mb-2">
              Tree stump grinding that leaves your yard clean and usable
            </h2>
            <p className="lead mb-0">
              Whether it’s one stump or an entire property, we grind below grade,
              haul away debris (optional), and help you reclaim your lawn—fast.
            </p>
          </Col>

          <Col lg={5}>
            <div
              className="p-4 rounded-4"
              style={{
                background: "rgba(87,199,133,0.12)",
                border: "1px solid rgba(87,199,133,0.35)",
              }}
            >
              <div className="d-flex flex-wrap gap-2">
                <span className="badge text-bg-light border">✔ Licensed & insured</span>
                <span className="badge text-bg-light border">✔ Tight-access capable</span>
                <span className="badge text-bg-light border">✔ Same-week scheduling</span>
                <span className="badge text-bg-light border">✔ Free quotes</span>
              </div>

              <div className="mt-3">
                <a className="btn btn-dark w-100" href="#quote">
                  Get a Free Quote
                </a>
                <div className="text-center fineprint mt-2">
                  Call/Text: <strong>{phoneNumber}</strong>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-4 mt-4">
          <Col md={6} lg={3}>
          {/* icon="🪵"  */}
            <FeatureCard
              
              title="Stump Grinding"
              desc="Grind stumps below grade so you can seed, sod, or replant."
            />
          </Col>
          <Col md={6} lg={3}>
          {/* icon="🌱" */}
            <FeatureCard
              
              title="Surface Roots"
              desc="Target surface roots that trip mowers or crack walkways."
            />
          </Col>
          <Col md={6} lg={3}>
          {/* icon="🚚" */}
            <FeatureCard
              
              title="Debris Haul-Off"
              desc="Keep the mulch or have us remove it—your choice."
            />
          </Col>

          <Col md={6} lg={3}>
          {/* icon="🧰" */}
            <FeatureCard
              
              title="Sight Restoration"
              desc="We can backfill the soil and reseed as best fits your requirements."
            />
          </Col>
        </Row>
      </Container>
    </section>
  );
}

function FeatureCard({ icon, title, desc }: { icon?: string; title: string; desc: string }) {
  return (
    <div className="feature-card p-4 h-100">
      {/* <div className="icon-badge mb-3">{icon}</div> */}
       {icon && <div className="icon-badge mb-3">{icon}</div>}
      <h3 className="h5 fw-bold">{title}</h3>
      <p className="mb-0 text-muted">{desc}</p>
    </div>
  );
}

function ProcessSection() {
  return (
    <section className="py-5 bg-light" id="process">
      <Container>
        <Row className="g-4 align-items-center">
          <Col lg={6}>
            <h2 className="section-title display-6 mb-3">How it works</h2>
            <ol className="fs-5">
              <li className="mb-2">
                <strong>Send photos</strong> (or request a visit) and we’ll confirm access and stump size.
              </li>
              <li className="mb-2">
                <strong>Get a clear quote</strong> based on diameter, quantity, and site conditions.
              </li>

              <li className="mb-2">
                <strong>Preparation</strong> - We call 811 Utility Locate, mark sprinkler heads and other potential hazards including
                <UI_ToolTipOnHover
                  text={<span className="text-brand-accent fw-bold"> <i>private utilities*</i> </span>}
                  tooltip="Private utilities requires property owner approval"
                  placement="top"
                />{" "}
                before we begin.
              </li>

              <li className="mb-2">
                <strong>We grind the stump</strong> below grade and clean up the area.
              </li>
              <li className="mb-0">
                <strong>Optional haul-off</strong> and final advice for seeding/sod/backfill.
              </li>
            </ol>
          </Col>

          <Col lg={6}>
            <div className="p-4 rounded-4 border bg-white">
              <h3 className="h5 fw-bold mb-2">What impacts price?</h3>
              <ul className="mb-0">
                <li>Stump diameter (measured at ground level)</li>
                <li>Quantity (multi-stump discounts)</li>
                <li>Access (gates, slopes, fences, stairs)</li>
                <li>Tree species/hardness and dirt/rocks</li>
                <li>Cleanup preference (leave grindings vs haul off)</li>
              </ul>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

function PricingSection({ phoneNumber, phoneHref }: { phoneNumber: string; phoneHref: string }) {
  return (
    <section className="py-5" id="pricing">
      <Container>
        <Row className="g-4 align-items-stretch">
          <Col lg={7}>
            <h2 className="section-title display-6 mb-2">Simple stump removal process</h2>
            <p className="lead">Most jobs can be quoted from photos. We deliver a clean, usable yard.</p>

            <Row className="g-3">
              <Col md={6}>
                <PricingCard title="Single stump" desc="Great for recent tree removals or one-off stumps in the lawn." />
              </Col>
              <Col md={6}>
                <PricingCard
                  title="Multiple stumps"
                  desc="Bundle pricing for properties with several stumps to grind."
                />
              </Col>
              <Col md={6}>
                <PricingCard
                  title="Tight-access jobs"
                  desc="Gated yards, slopes, or near structures—quoted based on access."
                />
              </Col>
              <Col md={6}>
                <PricingCard title="Additional Services" desc="Haul-off, backfill/topsoil, surface root grinding." />
              </Col>
            </Row>
          </Col>

          <Col lg={5}>
            <div className="cta-band p-4 p-lg-5 h-100">
              <h3 className="h4 fw-bold">Get a free quote today</h3>
              <p className="mb-3">
                Send a couple photos and we’ll respond quickly with pricing and next available times.
              </p>
              <div className="d-grid gap-2">
                <a className="btn btn-light btn-lg fw-bold" href="#quote">
                  Request a Quote
                </a>
                <a className="btn btn-outline-light btn-lg" href={phoneHref}>
                  Call {phoneNumber}
                </a>
              </div>
              <div className="mt-3 trust-badges">
                <span>
                  <span className="dot" />
                  Fast scheduling
                </span>
                <span>
                  <span className="dot" />
                  Clean finish
                </span>
                <span>
                  <span className="dot" />
                  Insured
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

function PricingCard({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="feature-card p-4 h-100">
      <h3 className="h5 fw-bold mb-1">{title}</h3>
      <p className="text-muted mb-0">{desc}</p>
    </div>
  );
}


function FAQSection({ faqs, serviceAreas }: { faqs: FaqItem[]; serviceAreas: string[] }) {
  const defaultOpenKey = useMemo(() => {
    const idx = faqs.findIndex((f) => f.defaultOpen);
    return idx >= 0 ? String(idx) : "0";
  }, [faqs]);

  return (
    <>
      
       <section className="py-5" id="recent-projects">
        <Container>
          <Row className="align-items-end g-4 mb-4">
            <Col lg={8}>
              <h2 className="section-title display-6 mb-2">Recent Projects</h2>
              <p className="lead mb-0">
                A few examples of the types of stump grinding and cleanup jobs we handle.
              </p>
            </Col>
            <Col lg={4}>
              <div className="text-lg-end">
                {/* <a className="btn btn-dark" href="#quote">
                  Request a Similar Quote
                </a> */}
              </div>
            </Col>
          </Row>

          <Row className="g-4">
            <Col md={6} lg={4}>
              <div className="feature-card p-4 h-100">
                {/* <div className="icon-badge mb-3">🏡</div> */}
                <h3 className="h5 fw-bold">Backyard stump removal</h3>
                <p className="mb-0 text-muted">
                  Removed an aging stump near a lawn area and ground below grade for a cleaner finish.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="feature-card p-4 h-100">
                {/* <div className="icon-badge mb-3">🌳</div> */}
                <h3 className="h5 fw-bold">Multi-stump property cleanup</h3>
                <p className="mb-0 text-muted">
                  Helped clear multiple stumps from a larger property to improve usability and appearance.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="feature-card p-4 h-100">
                {/* <div className="icon-badge mb-3">🚪</div> */}
                <h3 className="h5 fw-bold">Tight-access project</h3>
                <p className="mb-0 text-muted">
                  Completed a stump grinding job in a tighter access area after reviewing gate width and layout.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section> 
      
      
      {/* 

      // recient projects - original 
      <section className="py-5" id="recent-projects">
        <Container>
          <Row className="align-items-end g-4 mb-4">
            <Col lg={8}>
              <h2 className="section-title display-6 mb-2">Recent Projects</h2>
              <p className="lead mb-0">
                A few examples of the types of stump grinding and cleanup jobs we handle.
              </p>
            </Col>
            <Col lg={4}>
              <div className="text-lg-end">
                <a className="btn btn-dark" href="#quote">
                  Request a Similar Quote
                </a>
              </div>
            </Col>
          </Row>

          <Row className="g-4">
            <Col md={6} lg={4}>
              <div className="feature-card p-4 h-100">
                <div className="icon-badge mb-3">🏡</div>
                <h3 className="h5 fw-bold">Backyard stump removal</h3>
                <p className="mb-0 text-muted">
                  Removed an aging stump near a lawn area and ground below grade for a cleaner finish.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="feature-card p-4 h-100">
                <div className="icon-badge mb-3">🌳</div>
                <h3 className="h5 fw-bold">Multi-stump property cleanup</h3>
                <p className="mb-0 text-muted">
                  Helped clear multiple stumps from a larger property to improve usability and appearance.
                </p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="feature-card p-4 h-100">
                <div className="icon-badge mb-3">🚪</div>
                <h3 className="h5 fw-bold">Tight-access project</h3>
                <p className="mb-0 text-muted">
                  Completed a stump grinding job in a tighter access area after reviewing gate width and layout.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section> 
      
      */}

      <section className="py-5 bg-light" id="faq">
        <Container>
          <Row className="g-4">
            <Col lg={6}>
              <h2 className="section-title display-6 mb-3">FAQ</h2>

              <Accordion defaultActiveKey={defaultOpenKey}>
                {faqs.map((f, idx) => (
                  <Accordion.Item eventKey={String(idx)} key={f.id}>
                    <Accordion.Header>{f.question}</Accordion.Header>
                    <Accordion.Body>{f.answer}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>

            <Col lg={6}>
              <div
                className="p-4 rounded-4"
                style={{
                  background: "rgba(87,199,133,0.12)",
                  border: "1px solid rgba(87,199,133,0.35)",
                }}
              >
                <h3 className="h5 fw-bold mb-2">Service areas</h3>

                <ul className="row mb-4 list-unstyled">
                  {serviceAreas.map((area) => (
                    <li className="col-md-4" key={area}>
                      {area}
                    </li>
                  ))}
                </ul>

                <h3 className="h5 fw-bold mb-2">Ideal for</h3>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge text-bg-light border">Homeowners</span>
                  <span className="badge text-bg-light border">Landlords</span>
                  <span className="badge text-bg-light border">HOAs</span>
                  <span className="badge text-bg-light border">Real estate prep</span>
                  <span className="badge text-bg-light border">Landscapers</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

function QuoteSection({ locationConfig }: { locationConfig: ILocationConfig }) {
  const [status, setStatus] = useState<string>("");
  const [statusClass, setStatusClass] = useState<string>("small");
  const [submitted, setSubmitted] = useState<boolean>(false);

  const SMS_MARKETING_CONSENT_TEXT =
    `Marketing SMS consent (optional): By checking this box, you agree to receive marketing SMS text messages from ${locationConfig.businessName}. Msg & data rates may apply. Msg frequency varies. Reply STOP to opt out; HELP for help. Consent is not a condition of purchase.`;
  const SMS_NON_MARKETING_CONSENT_TEXT =
    `Non-marketing SMS consent (optional): By checking this box, you agree to receive non-marketing SMS text messages from ${locationConfig.businessName} about your inquiry (e.g., scheduling/updates). Msg & data rates may apply. Msg frequency varies. Reply STOP to opt out; HELP for help. Consent is not a condition of purchase.`;

  const STANDARD_AWS_API_URL =
    (typeof process !== "undefined" &&
      (process as any).env &&
      (process as any).env.REACT_APP_STANDARD_AWS_API_URL) ||
    (typeof window !== "undefined" && (window as any).STANDARD_AWS_API_URL) ||
    "";

  const [submitting, setSubmitting] = useState<boolean>(false);

  const [values, setValues] = useState<QuoteFormValues>({
    name: "",
    phone: "",
    email: "",
    city: "",
    count: "",
    diameter: "",
    access: "",
    message: "",
    smsMarketingConsent: false,
    smsNonMarketingConsent: false,
  });

  const [submittedValues, setSubmittedValues] = useState<QuoteFormValues | null>(null);

  const statusTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (statusTimerRef.current) window.clearTimeout(statusTimerRef.current);
    };
  }, []);

  const onChange =
    (field: Exclude<keyof QuoteFormValues, "smsMarketingConsent" | "smsNonMarketingConsent">) =>
    (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
      setValues((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const onSmsMarketingConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setValues((prev) => ({ ...prev, smsMarketingConsent: checked }));
  };

  const onSmsNonMarketingConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setValues((prev) => ({ ...prev, smsNonMarketingConsent: checked }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!STANDARD_AWS_API_URL) {
      setStatusClass("small text-danger");
      setStatus("Missing API configuration. Set REACT_APP_STANDARD_AWS_API_URL (or window.STANDARD_AWS_API_URL).");
      if (statusTimerRef.current) window.clearTimeout(statusTimerRef.current);
      statusTimerRef.current = window.setTimeout(() => setStatus(""), 6000);
      return;
    }

    try {
      setSubmitting(true);
      setStatusClass("small");
      setStatus("Submitting…");

      const smsOkToText = Boolean(values.smsMarketingConsent || values.smsNonMarketingConsent);

      const payload = {
        domainName: locationConfig.leadDomainName,
        dateTimeStamp: new Date().toISOString(),
        functionName: "setCustomerData",

        itemCategory: locationConfig.leadItemCategory,
        itemType: locationConfig.leadItemType,

        customerName: values.name,
        emailAddress: values.email,
        phone: values.phone,
        city: values.city,

        address: "",
        zip: "",
        utility: "",

        message: [
          `${locationConfig.businessName} Quote Request`,
          `Stump count: ${values.count || "n/a"}`,
          `Diameter: ${values.diameter || "n/a"}`,
          `Access: ${values.access || "n/a"}`,
          values.message ? `Notes: ${values.message}` : "",
        ]
          .filter(Boolean)
          .join(" | "),

        smsMarketingConsent: values.smsMarketingConsent,
        smsNonMarketingConsent: values.smsNonMarketingConsent,
        smsMarketingConsentText: SMS_MARKETING_CONSENT_TEXT,
        smsNonMarketingConsentText: SMS_NON_MARKETING_CONSENT_TEXT,
        smsOkToText,

        pageUrl: typeof window !== "undefined" ? window.location.href : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      };

      const wirePayload = toWirePayloadStrings(payload);

      const status = await postLead(STANDARD_AWS_API_URL, wirePayload);
      if (!status.ok) {
        throw new Error(status.message || `Request failed (HTTP ${status.status}).`);
      }

      setStatusClass("small text-success");
      setStatus("Thanks! We received your quote request. We’ll reach out shortly.");

      setSubmittedValues({ ...values });
      setSubmitted(true);

      setValues({
        name: "",
        phone: "",
        email: "",
        city: "",
        count: "",
        diameter: "",
        access: "",
        message: "",
        smsMarketingConsent: false,
        smsNonMarketingConsent: false,
      });
    } catch (err: any) {
      setStatusClass("small text-danger");
      setStatus(err?.message || "Sorry—something went wrong submitting your request.");
      setSubmitted(false);
    } finally {
      setSubmitting(false);
      if (statusTimerRef.current) window.clearTimeout(statusTimerRef.current);
      statusTimerRef.current = window.setTimeout(() => setStatus(""), 6000);
    }
  };

  const showSmsConsentHighlight =
    Boolean(values.phone) && !(values.smsMarketingConsent || values.smsNonMarketingConsent);

  return (
    <section className="py-5" id="quote">
      <Container>
        <Row className="g-4 align-items-start">
          <Col lg={6}>
            <h2 className="section-title display-6 mb-2">Request a free quote</h2>
            <p className="lead">
              Send the basics and we’ll get back fast. For the best estimate, include photos and stump diameter.
            </p>

            <div className="p-4 rounded-4 border">
              {submitted ? (
                <div>
                  <div className="p-3 rounded border" style={{ backgroundColor: "var(--bs-success-bg-subtle)" }}>
                    <h5>Thank you! A representative will be following up with you shortly.</h5>
                    <p><strong>Name:</strong> {(submittedValues?.name ?? values.name) || "—"}</p>
                    <p><strong>Email:</strong> {(submittedValues?.email ?? values.email) || "—"}</p>
                    <p><strong>Phone:</strong> {(submittedValues?.phone ?? values.phone) || "—"}</p>
                    <p>
                      <strong>SMS Opt-in (Marketing):</strong>{" "}
                      {(submittedValues?.smsMarketingConsent ?? values.smsMarketingConsent) ? "Yes" : "No"}
                    </p>
                    <p>
                      <strong>SMS Opt-in (Non-marketing):</strong>{" "}
                      {(submittedValues?.smsNonMarketingConsent ?? values.smsNonMarketingConsent) ? "Yes" : "No"}
                    </p>
                    <p className="mb-0"><strong>Message:</strong> {(submittedValues?.message ?? values.message) || "—"}</p>
                  </div>

                  <div className="mt-3 text-center">
                    <Button
                      variant="primary"
                      onClick={() => {
                        setSubmitted(false);
                        setSubmittedValues(null);
                        setStatus("");
                        setStatusClass("small");
                        window.setTimeout(() => {
                          const el = document.getElementById("quoteName") as HTMLInputElement | null;
                          el?.focus();
                        }, 50);
                      }}
                    >
                      Submit another request
                    </Button>

                    {status ? (
                      <div className={`${statusClass} mt-3`} aria-live="polite">
                        {status}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                <Form onSubmit={onSubmit}>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label className="form-label">Full Name</Form.Label>
                      <Form.Control
                        className="form-control"
                        type="text"
                        name="name"
                        id="quoteName"
                        placeholder="Your name"
                        required
                        value={values.name}
                        onChange={onChange("name")}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label className="form-label">Phone</Form.Label>
                      <Form.Control
                        className="form-control"
                        type="tel"
                        name="phone"
                        placeholder="(555) 555-5555"
                        required
                        value={values.phone}
                        onChange={onChange("phone")}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label className="form-label">Email (optional)</Form.Label>
                      <Form.Control
                        className="form-control"
                        type="email"
                        name="email"
                        placeholder="you@example.com"
                        value={values.email}
                        onChange={onChange("email")}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label className="form-label">City / Neighborhood</Form.Label>
                      <Form.Control
                        className="form-control"
                        type="text"
                        name="city"
                        placeholder="Where is the stump?"
                        required
                        value={values.city}
                        onChange={onChange("city")}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Label className="form-label">How many stumps?</Form.Label>
                      <Form.Select
                        className="form-select"
                        name="count"
                        required
                        value={values.count}
                        onChange={onChange("count")}
                      >
                        <option value="">Select…</option>
                        <option>1</option>
                        <option>2–3</option>
                        <option>4–6</option>
                        <option>7+</option>
                      </Form.Select>
                    </Col>

                    <Col md={6}>
                      <Form.Label className="form-label">Largest stump diameter</Form.Label>
                      <Form.Select
                        className="form-select"
                        name="diameter"
                        required
                        value={values.diameter}
                        onChange={onChange("diameter")}
                      >
                        <option value="">Select…</option>
                        <option>Under 12"</option>
                        <option>12"–24"</option>
                        <option>24"–36"</option>
                        <option>36"+</option>
                      </Form.Select>
                    </Col>

                    <Col xs={12}>
                      <Form.Label className="form-label">Access notes</Form.Label>
                      <Form.Control
                        className="form-control"
                        type="text"
                        name="access"
                        placeholder="Gate width, slope, stairs, near fence, etc."
                        value={values.access}
                        onChange={onChange("access")}
                      />
                    </Col>

                    <Col xs={12}>
                      <Form.Label className="form-label">Anything else?</Form.Label>
                      <Form.Control
                        as="textarea"
                        className="form-control"
                        name="message"
                        rows={4}
                        placeholder="Cleanup preference, timeline, photos link, etc."
                        value={values.message}
                        onChange={onChange("message")}
                      />
                      <div className="fineprint mt-2">
                        Tip: If you text photos, include a common object for scale (tape measure, shoe, etc.).
                      </div>
                    </Col>

                    <Col xs={12}>
                      <style>{`
                          #smsMarketingConsent,
                          #smsNonMarketingConsent {
                            border: 3px solid var(--bs-primary) !important;
                          }
                          .sms-consent-highlight {
                            border: 1px solid var(--bs-primary);
                            box-shadow: 0 0 0.5rem rgba(13,110,253,0.35);
                            border-radius: 0.75rem;
                            padding: 0.75rem;
                          }
                        `}</style>

                      <div className={showSmsConsentHighlight ? "sms-consent-highlight" : ""}>
                        <Form.Check
                          id="smsMarketingConsent"
                          type="checkbox"
                          checked={values.smsMarketingConsent}
                          onChange={onSmsMarketingConsentChange}
                          label={SMS_MARKETING_CONSENT_TEXT}
                        />
                        <div className="mt-2" />
                        <Form.Check
                          id="smsNonMarketingConsent"
                          type="checkbox"
                          checked={values.smsNonMarketingConsent}
                          onChange={onSmsNonMarketingConsentChange}
                          label={SMS_NON_MARKETING_CONSENT_TEXT}
                        />
                      </div>
                    </Col>

                    <Col xs={12} className="d-grid">
                      <Button className="btn btn-primary btn-lg" type="submit" disabled={submitting}>
                        {submitting ? "Sending…" : "Send Quote Request"}
                      </Button>
                       <p className="fineprint mt-3 mb-0 text-center small">
                          By submitting, you agree we may contact you about your request. 
                        </p>
                    </Col>


                  <div className="mx-auto small text-center">
                    <Link to="/tos" className="text-decoration-none">
                      Terms of Service
                    </Link>
                    <span className="mx-2">•</span>
                    <Link to="/privacy" className="text-decoration-none">
                      Privacy Policy
                    </Link>
                  </div>




                    <Col xs={12}>
                      <div id="quoteStatus" className={statusClass}>
                        {status}
                      </div>
                    </Col>
                  </Row>
                </Form>
              )}
            </div>

           
          </Col>

          <Col lg={6}>
            <div className="p-4 rounded-4 bg-light border h-100">
              <h3 className="h5 fw-bold mb-2">Want the fastest quote?</h3>
              <ul className="mb-4">
                <li>Upload 2–4 photos (wide + close)</li>
                <li>Tell us stump diameter at ground level</li>
                <li>Confirm gate width / access</li>
                <li>Let us know if you want chips hauled off</li>
              </ul>

              <div className="p-4 rounded-4 bg-white border">
                <h3 className="h5 fw-bold mb-2">Contact</h3>
                <p className="mb-1">
                  <strong>Phone:</strong> {locationConfig.phoneNumber}{" "}
                </p>
                <p className="mb-1">
                  <strong>Email:</strong> {locationConfig.email}
                </p>
                <p className="mb-0">
                  <strong>Hours:</strong> {locationConfig.hours}
                </p>
              </div>

              <div
                className="mt-4 p-4 rounded-4"
                style={{
                  background: "rgba(247,201,72,0.18)",
                  border: "1px solid rgba(247,201,72,0.55)",
                }}
              >
                <h3 className="h5 fw-bold mb-2">Seasonal note</h3>
                <p className="mb-0">
                  {locationConfig.seasonalNote}
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

// export default function EasyStumpOut_HomePage_v01(aLocationConfig:ILocationConfig) {

export default function EasyStumpOut_HomePage_v01( {locationConfig}: locationConfigProps) {

// const locationConfig = DEFAULT_EASY_STUMP_OUT_LOCATION;
  
  useInjectHeadMetadata(locationConfig);

  const HERO_CAROUSEL: HeroCarouselData = useMemo(
    () => ({
      brandKicker: locationConfig.brandKicker,
      slides: [
        {
          title: "Remove stumps fast — reclaim your yard",
          description:
            "Professional stump grinding that leaves a clean, usable space for grass, landscaping, or replanting.",
          imageUrl: "images-easystumpout/AdobeStock_891464091.jpg",
          primaryCtaText: "Get a Free Quote",
          primaryCtaHref: "#quote",
          secondaryCtaText: "Call Now",
          secondaryCtaHref: locationConfig.phoneHref,
        },
        {
          title: "Clean finish, below-grade grinding",
          description:
            "We grind below the surface so your mower, kids, and landscaping plans aren’t blocked by an old stump.",
          imageUrl: "images-easystumpout/AdobeStock_559004299.jpg",
          primaryCtaText: "See Services",
          primaryCtaHref: "#services",
          secondaryCtaText: "How It Works",
          secondaryCtaHref: "#process",
        },
        {
          title: "Tight-access and tricky locations",
          description:
            "Gated yards, slopes, near fences—tell us the details and we’ll confirm the safest approach.",
          imageUrl: "images-easystumpout/AdobeStock_1613124365.jpg",
          primaryCtaText: "Request a Quote",
          primaryCtaHref: "#quote",
          secondaryCtaText: "Pricing Factors",
          secondaryCtaHref: "#pricing",
        },
        {
          title: "Multiple stumps? Bundle pricing available",
          description:
            "Property cleanups and multi-stump jobs are our specialty—save when we grind several at once.",
          imageUrl: "images-easystumpout/ColoradoStump01.jpg",
          primaryCtaText: "Get Bundle Quote",
          primaryCtaHref: "#quote",
          secondaryCtaText: "FAQ",
          secondaryCtaHref: "#faq",
        },
        {
          title: "Debris haul-off (optional)",
          description:
            "Keep the grindings as mulch or have us remove them—either way, we leave the site tidy.",
          imageUrl: "images-easystumpout/ColoradoStump02CS.jpg",
          primaryCtaText: "Free Quote",
          primaryCtaHref: "#quote",
          secondaryCtaText: `Call ${locationConfig.phoneNumber}`,
          secondaryCtaHref: locationConfig.phoneHref,
        },
      ],
    }),
    [locationConfig]
  );

  const faqs: FaqItem[] = useMemo(
    () => [
      {
        id: "faq1",
        question: "How deep do you grind the stump?",
        defaultOpen: true,
        answer: (
          <>
            Typically several inches below grade (and deeper if needed for your project). Tell us if you’re planting,
            building, or installing turf.
          </>
        ),
      },
      {
        id: "faq2",
        question: "Do you remove the wood chips?",
        answer: <>We can leave the grindings (often useful as mulch), backfill the hole, reseed or haul them away—your choice.</>,
      },
      {
        id: "faq3",
        question: "Is stump grinding safe near fences or foundations?",
        answer: (
          <>
            In many cases, yes—site conditions matter. We’ll confirm clearance, access, and any risks before starting.
          </>
        ),
      },
      {
        id: "faq4",
        question: "What do you need from me before you arrive?",
        answer: (
          <>
            Please mark sprinkler heads. Clear small items around the stump (rocks, toys, decor) if possible.
            <b><i> We will be sure to call 811 Utility Locate to ensure a safe operation.</i></b>
          </>
        ),
      },
      {
        id: "faq5",
        question: "Will the stump grow back after grinding?",
        answer: (
          <>
            In most cases, grinding prevents regrowth because the stump is removed below grade. Some species can sprout
            from remaining roots, so if regrowth is a concern we can discuss options (deeper grind or treatment).
          </>
        ),
      },
      {
        id: "faq6",
        question: "How long does stump grinding take?",
        answer: (
          <>
            Many single stumps can be completed in under an hour once we’re set up. Larger stumps, multiple stumps, rocky
            soil, or tight access can take longer—your quote will include a realistic time estimate.
          </>
        ),
      },
      {
        id: "faq7",
        question: "What happens to the hole after the stump is ground?",
        answer: (
          <>
            The ground stump turns into wood chips mixed with soil. We can leave the grindings to settle or use as mulch,
            or you can choose backfill/topsoil (or haul-off) if you want the area ready for sod or seed right away.
          </>
        ),
      },
    ],
    []
  );

  const serviceAreas = useMemo(() => locationConfig.serviceAreas, [locationConfig]);

  return (
    <>
      <InlinePageStyles />

      <EasyStumpOut_Header locationConfig={locationConfig} />

      <HeroCarousel data={HERO_CAROUSEL} />

      <main>
        <ServicesSection phoneNumber={locationConfig.phoneNumber} />
        <ProcessSection />
        <PricingSection
          phoneNumber={locationConfig.phoneNumber}
          phoneHref={locationConfig.phoneHref}
        />

        
        


        <FAQSection faqs={faqs} serviceAreas={serviceAreas} />
        <QuoteSection locationConfig={locationConfig} />
      </main>

      <EasyStumpOut_Footer locationConfig={locationConfig} />
    </>
  );
}

/* =============================================================================
File: EasyStumpOut_HomePage_v01.tsx
Description: Homepage component preserving original content/layout with ReactSetv22026BSSC split structure.
Version: v01
Date: 2026-04-14
============================================================================= */
